import { buildUrl, isConnectMessageType, unwrap } from './utils';
import { openInIframe } from '@shared/iFrameHandler/openInIframe';
import { isTroubleshootFlow } from '@idConnect/identification/troubleshooting/handleTroubleshootService';
import { denyConsent, idConnectCallbackSuccess } from './actions';
import { handleIdentificationFailure } from '@idConnect/identification/handlers/connect/handleIdentificationFailure';
import { Maker } from './buildMachine';
import { Ctx, Snap } from './core';
import { ConnectMessageType } from '@idConnect/types';
import { addDays } from 'date-fns/addDays';
import { getHostname } from '@shared/getUtiqHost';
import { callEventListeners } from '@shared/events/callEventListeners';

export const connect: Maker<Ctx, Snap, ConnectMessageType> = ({
  makeEffect,
  makeEffectGroup,
}) => [
  makeEffect(
    'SEND',
    (msg, { snap, pass, skip, ctx }) => {
      const connectId =
        ctx.state.resolvingConnectId ?? snap.localStorage.utiqPass?.connectId;

      if (msg.status === 'SEND' && connectId) {
        const exchangeMessageData: {
          msgType: string;
          status: string;
          data: {
            transactionId: string;
            connectId: string;
            connectDomain: string;
            umdid: string;
          };
        } = {
          msgType: 'CONNECT',
          status: 'EXCHANGE',
          data: {
            transactionId: ctx.window.Utiq!.transactionId!,
            connectId: connectId.value,
            connectDomain: connectId.domain,
            umdid: '',
          },
        };
        return pass(exchangeMessageData);
      }

      return skip('');
    },
    (exchangeMessageData, { ctx }) => {
      const { evt } = ctx.iframeMsgs.shift()!;

      evt.source?.postMessage(JSON.stringify(exchangeMessageData), {
        targetOrigin: '*',
      });
    },
  ),

  makeEffect(
    'NOUMDID',
    (msg, { snap, pass, skip, ctx }) => {
      const connectId =
        ctx.state.resolvingConnectId ?? snap.localStorage.utiqPass?.connectId;

      if (msg.status === 'NOUMDID' && connectId) {
        const successData = {
          connectId: connectId,
          ids: {
            source: {
              connectId: connectId,
              domain: new URL(window.IDCONNECT_CONFIG?.IDCONNECT_API_HOST ?? '')
                .hostname,
              umdid: null as any,
              originalUmdidCookie: null as any,
              dataOriginalUmdidCookie: {},
            },
            target: {
              connectId: null,
              domain: null,
              umdid: null,
              umdidCookie: null,
            } as any,
          },
          connectStatus: 'NOUMDID',
        };

        return pass(successData);
      }

      return skip('');
    },

    (successData, { ctx, snap }) => {
      ctx.iframeMsgs.shift();
      idConnectCallbackSuccess(snap, successData);
    },
  ),

  makeEffectGroup(
    'DONE',
    (msg, { pass, skip }) => (msg.status === 'DONE' ? pass(msg) : skip('')),
    ({ makeEffect }) => [
      makeEffect(
        'isTroubleshootFlow',
        (msg, { cond }) =>
          cond(
            msg?.body?.status?.StatusC1 === 'NotCreated' &&
              isTroubleshootFlow(msg, 'error'),

            `msg?.body?.status?.StatusC1 === 'NotCreated' && isTroubleshootFlow(msg, 'error')`,
            msg?.body?.status?.StatusC1 === 'NotCreated',
            isTroubleshootFlow(msg, 'error'),
          ),
        (_, { ctx }) => {
          ctx.iframeMsgs.shift();
          callEventListeners('onFlowCompleted', 'Utiq', { status: 'error' });
        },
      ),

      makeEffect(
        'blacklisted',
        (msg, { pass, skip, snap }) => {
          if (msg?.body?.status?.Reason !== 'blacklisted') return skip('');

          const event =
            snap.localStorage.utiqPass === null ? null : 'ids_not_updated';

          return pass({ event });
        },

        ({ event }, { ctx, snap }) => {
          // This conditional SHOUL NOT BE HERE!
          // previously, multiple places were sending ids_not_set messages in this flow.
          // In the other is in handleIframeSrcDoc::204
          // We need to have an overahaul of where analytics are managed and how they are triggered
          // but for now I've just made this analytics call only triggered for ids_not_update
          if (event) ctx.analytics[event] = snap.now;

          ctx.iframeMsgs.shift();
          unwrap(ctx.window.Utiq).utiqFlowStarted = false;
          denyConsent(snap, ctx);
          callEventListeners('onConsentChanging', 'Utiq', {
            isConsentGranted: false,
          });
        },
      ),

      makeEffect(
        'serviceBlocked',
        (msg, { pass, skip, snap }) => {
          if (msg?.body?.status?.StatusC1 === 'NotCreated') {
            return pass(
              {
                ...snap.localStorage.utiqEligibility,
                isEligible: false,
                retryEligibilityCheckTime: addDays(snap.now, 7).getTime(),
              },
              'Telco identification call failed. Identification will be checked again in 7 days...',
            );
          }

          return skip('');
        },

        (utiqEligibility, { ctx }) => {
          ctx.iframeMsgs.shift();
          ctx.window.localStorage.setItem(
            'utiqEligibility',
            JSON.stringify(utiqEligibility),
          );
          callEventListeners('onEligibilityChecked', 'Utiq', {
            isEligible: false,
          });
        },
      ),

      makeEffect(
        'success',

        (msg, { ctx, skip, pass }) => {
          if (!isConnectMessageType(msg))
            return skip('!isConnectMessageType(msg)');
          const iFrameUrl = buildUrl(ctx, ['idConnectConfig'])
            ?.tryPathFrom(
              ctx.window.Utiq?.data?.clientConfig?.IDCONNECT_CONFIG
                ?.IDCONNECT_API_HOST,
            )
            ?.addPathSegment('data')
            .render();

          if (!iFrameUrl) return skip('url unbuildable');

          return pass({ msg, iFrameUrl });
        },
        ({ msg, iFrameUrl }, { ctx, snap }) => {
          ctx.iframeMsgs.shift();

          openInIframe({
            iFrameUrl,
            iFrameId: 'GETDATA',
            environment: 'Utiq',
          });

          msg.body.ids.source.originalUmdidCookie = null as any;

          const connectId = {
            ...ctx.state.resolvingConnectId,
            consents: [] as [],
            idGraph: [] as [],
          };
          delete connectId.createdDate;
          const idConnectSuccessCallbackData = {
            connectId,
            ids: msg.body.ids,
            connectStatus: msg.body.status.StatusC1,
          };

          idConnectCallbackSuccess(snap, idConnectSuccessCallbackData as any);
        },
      ),
    ],
  ),

  makeEffectGroup(
    'ERROR',
    (msg, { pass, skip }) => {
      if (msg.status === 'ERROR') {
        return pass(msg);
      }
      return skip('msg.status', msg.status);
    },

    ({ makeEffect }) => [
      // these two flows differ because isTroubleshootFlow fires ids_not_set analytics and calls onFlowComplete

      makeEffect(
        'isTroubleshootFlow',
        (msg, { pass, skip }) => {
          if (isTroubleshootFlow(msg, 'error')) return pass(msg);
          return skip('');
        },
        (_, { ctx, snap }) => {
          ctx.iframeMsgs.shift();
          handleIdentificationFailure();
          localStorage.removeItem('utiqPass');
          localStorage.removeItem('utiq_openrtb_eids');
          callEventListeners('onFlowCompleted', 'Utiq', {
            status: 'error',
          });
          callEventListeners('onEligibilityChecked', 'Utiq', {
            isEligible: false,
          });
          ctx.analytics.ids_not_set = snap.now;
        },
      ),

      makeEffect(
        'isNormalFlow',
        (msg, { pass }) => pass(msg),
        (_, { ctx }) => {
          ctx.iframeMsgs.shift();
          handleIdentificationFailure();
          localStorage.removeItem('utiqPass');
          localStorage.removeItem('utiq_openrtb_eids');
          callEventListeners('onEligibilityChecked', 'Utiq', {
            isEligible: false,
          });
        },
      ),
    ],
  ),

  makeEffect(
    'NOLOCALSTORAGEACCESS',
    (msg, { cond }) =>
      cond(
        msg.status === 'NOLOCALSTORAGEACCESS' && msg,
        'msg.status',
        msg.status,
      ),
    (_, { ctx }) => {
      ctx.iframeMsgs.shift();
    },
  ),
];
